import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import withRoleAccess from '../../hoc/withRoleAccess';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Table, Tabs, Input, Button, Card, Typography, Space, Modal, Form, InputNumber, Tag, Row, Col, Divider } from 'antd';
import { SearchOutlined, ExclamationCircleOutlined, BookOutlined, UserOutlined, TeamOutlined } from '@ant-design/icons';
import TeacherPaymentReport from './TeacherPaymentReport';
import TutorClassLogPayment from './TutorClassLogPayment';

const { Title } = Typography;

const TeacherHoursAdmin = () => {
  const { t } = useTranslation();
  const [teachers, setTeachers] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [teacherRates, setTeacherRates] = useState([]);
  const [subjectGroups, setSubjectGroups] = useState([]);
  const [classSizeCategories, setClassSizeCategories] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  const itemsPerPage = 100;
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');

        const [teachersRes, ratesRes, subjectsRes, categoriesRes] = await Promise.all([
          axios.get(`${API_URL}/teachers`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/teacherrate`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/teacherrate/subjects`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/teacherrate/class-size-categories`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        ]);

        setTeachers(teachersRes.data.teachers || []);
        setTeacherRates(ratesRes.data);
        setSubjectGroups(subjectsRes.data);
        setClassSizeCategories(categoriesRes.data);
      } catch (error) {
        console.error(t('errorFetchingTeachers'), error);
        setError(t('failedToFetchTeachers'));
      }
    };

    fetchData();
  }, [API_URL, t]);

  const openRateForm = (teacher) => {
    setSelectedTeacher(teacher);
    
    // Get existing rates for this teacher
    const teacherExistingRates = teacherRates.filter(r => r.teacher_id === teacher.userid);
    
    // Create initial form values with existing rates
    const initialValues = {};
    subjectGroups.forEach(subject => {
      classSizeCategories.forEach(category => {
        const existingRate = teacherExistingRates.find(r => 
          r.subject_id === subject.id && r.class_size_category === category.value
        );
        const fieldKey = `${subject.id}_${category.value}`;
        initialValues[fieldKey] = existingRate ? existingRate.rate : 0;
      });
    });
    
    form.setFieldsValue(initialValues);
    setIsModalVisible(true);
  };

  const handleRateSubmit = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');

      // Prepare rates data for bulk creation/updating
      const ratesToSubmit = [];
      
      subjectGroups.forEach(subject => {
        classSizeCategories.forEach(category => {
          const fieldKey = `${subject.id}_${category.value}`;
          const rate = values[fieldKey];
          
          if (rate && rate > 0) {
            ratesToSubmit.push({
              teacher_id: selectedTeacher.userid,
              subject_id: subject.id,
              class_size_category: category.value,
              rate: rate
            });
          }
        });
      });

      // Delete existing rates for this teacher first
      const existingRates = teacherRates.filter(r => r.teacher_id === selectedTeacher.userid);
      await Promise.all(
        existingRates.map(rate =>
          axios.delete(`${API_URL}/teacherrate/${rate.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );

      // Create new rates
      await Promise.all(
        ratesToSubmit.map(rateData =>
          axios.post(`${API_URL}/teacherrate`, rateData, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );

      // Refresh rate list
      const res = await axios.get(`${API_URL}/teacherrate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeacherRates(res.data);
      setIsModalVisible(false);
    } catch (err) {
      console.error('Error submitting rates:', err);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const filteredTeachers = teachers.filter(
    (teacher) =>
      teacher.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      teacher.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      teacher.nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      teacher.userid.toString().includes(searchTerm)
  );

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTeachers = filteredTeachers.slice(startIndex, endIndex);

  // Helper function to get icon for class size
  const getClassSizeIcon = (category) => {
    switch(category) {
      case 'private_1_2': return <UserOutlined />;
      case 'group_3_5': return <TeamOutlined />;
      case 'group_6_10': return <TeamOutlined />;
      default: return <UserOutlined />;
    }
  };

  // Helper function to get color for class size
  const getClassSizeColor = (category) => {
    switch(category) {
      case 'private_1_2': return 'red';
      case 'group_3_5': return 'blue';
      case 'group_6_10': return 'green';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: t('Teacher ID'),
      dataIndex: 'userid',
      key: 'userid',
      sorter: (a, b) => a.userid - b.userid,
    },
    {
      title: t('teacherName'),
      dataIndex: 'name',
      key: 'name',
      render: (_, teacher) => (
        <Link to={`/teacher-details/${teacher.userid}`}>
          ({teacher.nickname}) {teacher.firstname} {teacher.lastname}
        </Link>
      ),
    },
    {
      title: t('Subject Group Rates'),
      key: 'rates',
      render: (_, teacher) => {
        const teacherRatesForSubjects = teacherRates.filter(r => r.teacher_id === teacher.userid);
        
        if (teacherRatesForSubjects.length === 0) {
          return (
            <Tag color="default" icon={<ExclamationCircleOutlined />}>
              {t('No rates set')}
            </Tag>
          );
        }

        // Group rates by subject
        const ratesBySubject = {};
        teacherRatesForSubjects.forEach(rate => {
          if (!ratesBySubject[rate.subject_id]) {
            ratesBySubject[rate.subject_id] = {};
          }
          ratesBySubject[rate.subject_id][rate.class_size_category] = rate.rate;
        });

        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {subjectGroups.map(subject => {
              const subjectRates = ratesBySubject[subject.id];
              if (!subjectRates) return null;

              return (
                <div key={subject.id} style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#666', marginBottom: '4px' }}>
                    <BookOutlined /> {subject.name}
                  </div>
                  <Space wrap size="small">
                    {classSizeCategories.map(category => {
                      const rate = subjectRates[category.value];
                      if (!rate) return null;
                      
                      return (
                        <Tag 
                          key={category.value}
                          color={getClassSizeColor(category.value)} 
                          icon={getClassSizeIcon(category.value)}
                          style={{ fontSize: '11px' }}
                        >
                          {category.label}: ฿{rate}
                        </Tag>
                      );
                    })}
                  </Space>
                </div>
              );
            })}
          </Space>
        );
      }
    },
    {
      title: t('Action'),
      key: 'action',
      render: (_, teacher) => {
        const teacherRatesCount = teacherRates.filter(r => r.teacher_id === teacher.userid).length;
        const totalPossibleRates = subjectGroups.length * classSizeCategories.length;
        
        return (
          <Space direction="vertical" size="small">
            <Button onClick={() => openRateForm(teacher)}>
              {teacherRatesCount > 0 ? t('Edit Rates') : t('Add Rates')}
            </Button>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {teacherRatesCount}/{totalPossibleRates} rates set
            </div>
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      <Sidebar />
      <Header />
      <div style={{ padding: '16px', marginLeft: '70px', marginTop: '50px' }}>

        <Card style={{ marginTop: 16 }}>
          <Title level={3}>{t('Teacher Hours')}</Title>

          {error && <p style={{ color: 'red' }}>{error}</p>}

          <Tabs
            defaultActiveKey="hours"
            items={[
              {
                label: t('Teacher Hours'),
                key: 'hours',
                children: (
                  <>
                  <div style={{ padding: 24 }}>
                    <Space style={{ marginBottom: 24 }}>
                      <Input
                        placeholder={t('searchByNameOrID')}
                        prefix={<SearchOutlined />}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        style={{ width: 300 }}
                      />
                    </Space>
                    
                    <Table
                      dataSource={currentTeachers}
                      columns={columns}
                      pagination={{
                        current: currentPage,
                        pageSize: itemsPerPage,
                        total: filteredTeachers.length,
                        onChange: (page) => setCurrentPage(page),
                      }}
                      rowKey="userid"
                      scroll={{ x: 'max-content' }}
                    />
                    </div>
                  </>
                ),
              },
              {
                label: t('Teacher Class Logs'),
                key: 'logs',
                children: <TutorClassLogPayment />,
              },
              {
                label: t('paymentReport'),
                key: 'payment',
                children: <TeacherPaymentReport />,
              }
            ]}
          />

        </Card>
      </div>

      <Modal
        title={selectedTeacher ? `${t('Teacher Rates')}: ${selectedTeacher.firstname} ${selectedTeacher.lastname}` : t('Teacher Rates')}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleRateSubmit}
        destroyOnClose
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
        >
          {subjectGroups.map(subject => (
            <div key={subject.id}>
              <Divider orientation="left">
                <BookOutlined /> {subject.name}
              </Divider>
              
              <Row gutter={[16, 16]}>
                {classSizeCategories.map(category => (
                  <Col span={8} key={category.value}>
                    <Form.Item
                      name={`${subject.id}_${category.value}`}
                      label={
                        <Space>
                          {getClassSizeIcon(category.value)}
                          {category.label}
                        </Space>
                      }
                      rules={[{ required: false }]}
                    >
                      <InputNumber 
                        min={0} 
                        prefix="฿" 
                        style={{ width: '100%' }}
                        placeholder="Enter rate"
                      />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </Form>
      </Modal>

    </div>
  );
};

export default withRoleAccess(TeacherHoursAdmin, ['superadmin']);